/**
 * Serialize one nested sprint (and any nested sprints inside its tasks).
 * Task ids become indexes into this sprint's own task array.
 */

import { omitEmpty, cleanIcon, buildTagsArray } from '../constants';
import { isV2Format } from '../../../../shared/ConditionsDropdown/utils/conditionHelpers';
import { getWaitSeconds } from '../../../../shared/ConditionsDropdown/utils/conditionWait';
import { DEFAULT_OPTIONS } from '../../../TaskNode/DecisionOptionsModal';
import {
  serializeDependsOnIndex,
  serializeAttachedDocs,
  serializeDocTicks,
  serializeDocFiles,
  serializeVariableValues,
  serializeMilestoneAnchor,
  serializeNoteAnchor,
} from './payload';

export function serializeNestedSprintEntry(sprint, forTaskIndex, nestedSprintData) {
  const hasTasks = Array.isArray(sprint.tasks);
  let serializedTasks;
  if (hasTasks) {
    const nestedIdToIndex = new Map();
    sprint.tasks.forEach((t, i) => nestedIdToIndex.set(t.id, i));

    serializedTasks = sprint.tasks.map((t) => {
      const depIndices = (Array.isArray(t.dependsOn) ? t.dependsOn : [])
        .map(dep => {
          const depTaskId = typeof dep === 'string' ? dep : dep.taskId;
          const depIndex = nestedIdToIndex.get(depTaskId);
          if (depIndex === undefined) return null;
          return serializeDependsOnIndex(dep, depIndex, () => sprint.tasks.find(st => st.id === depTaskId));
        })
        .filter(d => d !== null);

      let ucIndices;
      if (isV2Format(t.unlockConditions)) {
        const blocks = t.unlockConditions.blocks.map(block => {
          const conds = block.conditions.map(cond => {
            const di = nestedIdToIndex.get(cond.taskId);
            if (di === undefined) return null;
            let branchIdx;
            if (cond.branch) {
              const depTask = sprint.tasks.find(st => st.id === cond.taskId);
              const depOpts = depTask?.options?.length > 0 ? depTask.options : DEFAULT_OPTIONS;
              const idx = depOpts.findIndex(o => o.id === cond.branch);
              if (idx >= 0) branchIdx = idx;
            }
            return omitEmpty({ task: di, branch: branchIdx, waitSeconds: getWaitSeconds(cond) });
          }).filter(Boolean);
          return omitEmpty({ conditions: conds.length > 0 ? conds : undefined, innerOperator: block.innerOperator, outerOperator: block.outerOperator });
        }).filter(b => b.conditions && b.conditions.length > 0);
        ucIndices = blocks.length > 0 ? { version: 2, blocks } : undefined;
      } else if (Array.isArray(t.unlockConditions) && t.unlockConditions.length > 0) {
        const flatIndices = t.unlockConditions
          .map(cond => {
            const di = nestedIdToIndex.get(cond.taskId);
            if (di === undefined) return null;
            let branchIdx;
            if (cond.branch) {
              const depTask = sprint.tasks.find(st => st.id === cond.taskId);
              const depOpts = depTask?.options?.length > 0 ? depTask.options : DEFAULT_OPTIONS;
              const idx = depOpts.findIndex(o => o.id === cond.branch);
              if (idx >= 0) branchIdx = idx;
            }
            return omitEmpty({ task: di, operator: cond.operator, branch: branchIdx, waitSeconds: getWaitSeconds(cond) });
          })
          .filter(c => c !== null);
        ucIndices = flatIndices.length > 0 ? flatIndices : undefined;
      }

      let options;
      let answer;
      if (t.nodeType === 'decision') {
        const opts = t.options?.length > 0 ? t.options : DEFAULT_OPTIONS;
        options = opts.map(o => omitEmpty({ label: o.label, color: o.color }));
        if (t.answer) {
          const aIdx = opts.findIndex(o => o.id === t.answer);
          if (aIdx >= 0) answer = aIdx;
        }
      }

      const attachedDocs = serializeAttachedDocs(t);

      return omitEmpty({
        name: t.name,
        status: (t.status && t.status !== 'todo' && t.status !== 'pending') ? t.status : undefined,
        nodeType: t.nodeType && t.nodeType !== 'task' ? t.nodeType : undefined,
        priority: t.priority,
        difficulty: t.difficulty,
        tags: buildTagsArray(t),
        icon: cleanIcon(t.icon),
        notes: t.notes,
        internalNotes: t.internalNotes,
        dueDate: t.dueDate,
        estimatedDurationMinutes: t.estimatedDurationMinutes,
        reminders: (t.reminders || []).length > 0 ? t.reminders : undefined,
        subtasks: (t.subtasks || []).length > 0
          ? t.subtasks.map(st => omitEmpty({ name: st.name, completed: st.completed || undefined }))
          : undefined,
        deliverables: (t.deliverables || []).length > 0
          ? t.deliverables.map(d => omitEmpty({ name: d.name, completed: d.completed || undefined }))
          : undefined,
        inputs: (t.inputs || []).filter(i => i.sourceType !== 'mission').length > 0
          ? t.inputs.filter(i => i.sourceType !== 'mission').map(i => omitEmpty({ name: i.name, completed: i.completed || undefined }))
          : undefined,
        options,
        answer,
        attachedDocs,
        docTicks: serializeDocTicks(t, attachedDocs),
        docFiles: serializeDocFiles(t, attachedDocs),
        variableValues: serializeVariableValues(t),
        // Migration marker rides along so a pasted copy of a migrated mission
        // doesn't re-run the legacy notes→docs migration (which would mint a
        // fresh doc from the frozen pre-migration content on every paste)
        docsMigrated: t.docsMigratedAt ? true : undefined,
        milestoneAnchor: serializeMilestoneAnchor(t, nestedIdToIndex),
        noteAnchor: serializeNoteAnchor(t, nestedIdToIndex),
        dependsOn: depIndices.length > 0 ? depIndices : undefined,
        unlockConditions: ucIndices || undefined,
        hasNestedSprint: t.nestedSprintId ? true : undefined,
      });
    });
  }

  const childNestedSprints = (sprint.tasks || [])
    .map((t, tIdx) => {
      if (!t.nestedSprintId || !nestedSprintData[t.id]) return null;
      return serializeNestedSprintEntry(nestedSprintData[t.id], tIdx, nestedSprintData);
    })
    .filter(Boolean);

  return omitEmpty({
    forTask: forTaskIndex,
    name: sprint.name,
    icon: cleanIcon(sprint.icon),
    tasks: serializedTasks,
    nestedSprints: childNestedSprints.length > 0 ? childNestedSprints : undefined,
    statuses: sprint.statuses,
    categories: sprint.categories,
    tags: sprint.tags,
    groups: sprint.groups,
    // Chain order is a live canvas preference; IDs would not survive paste remap.
    customFields: sprint.customFields,
    // Campaign-scoped variables — must survive copy/paste (same as Ctrl+D)
    variables: Array.isArray(sprint.variables) && sprint.variables.length > 0
      ? sprint.variables.map((v) => ({
          id: v.id,
          name: v.name,
          value: v.value,
          type: v.type || undefined,
          values: Array.isArray(v.values) ? [...v.values] : undefined,
          description: v.description,
          color: v.color,
          locked: v.locked || undefined,
        }))
      : undefined,
    kanbanColumnOrder: sprint.kanbanColumnOrder,
    cardDisplaySettings: sprint.cardDisplaySettings,
    views: sprint.views,
    defaultViewSettings: sprint.defaultViewSettings,
  });
}
